import React, { createContext, useContext, useState } from "react";
import PropTypes from "prop-types";
import { format } from "date-fns";
import useLogger from "../util/Logger";
import { useAuth } from "./AuthHook";

const EngineerAvailabilityContext = createContext();

const compareDates = (date1) => (date2) => date2.AvailabilityDate.slice(0, 10) === date1.EventDate.slice(0, 10);

const stripAndLowercase = (str) => str.toLowerCase().replace(/\s+/g, '');

const isRecommended = (payload, siteId, projectId, workItemTemplateRef) => {
  try {
    return payload.some((slot) => slot.SiteId === siteId && workItemTemplateRef === "DomesticFieldReactive" ||
      slot.ProjectId === projectId && slot.SiteId === siteId && (
        workItemTemplateRef === "DomesticProject-PropertyInstallation" ||
        workItemTemplateRef === "DomesticCMV" ||
        workItemTemplateRef === "DomesticProject-PropertyOptimiseException" ||
        workItemTemplateRef === "DomesticProject-PropertyMeterException"
      ));
  } catch (error) {
    return false;
  }
}

const isRecommendedText = (payload, siteId, projectId, workItemTemplateRef) => {
  let text = "";
  try {
    if (!payload.some((slot) => slot.SiteId === siteId)) {
      text = "the siteId does not match any agreed appointments in svis";
    };

    if (workItemTemplateRef !== "DomesticFieldReactive") {
      if (text === "") {
        text = "the template is not DomesticFieldReactive";
      } else {
        text += " and the template is not DomesticFieldReactive";
      };
    };

    return text;
  } catch (error) {
    return "error in catch";
  }
}

const flattenPayload = (array) => array.reduce((acc, next) => ({
  AM: [...acc.AM, ...next.AM],
  PM: [...acc.PM, ...next.PM],
}), { AM: [], PM: [] })

export default function EngineerAvailabilityProvider({ children }) {
  const logger = useLogger();
  const auth = useAuth();

  const [currentEngineerAvailability, setEngineerAvailability] = useState([]);

  const updateEngineerAvailabilityV2 = async ({ territory, siteId, projectId, date = new Date(), urgent, workItemTemplateRef }) => {
    try {
      const typeParam = projectId.includes("PRJ") ? `&type=project` : "";

      const svisData = await auth.get(
        encodeURI(
          `field/engineerAvailability?territory=${territory}&date=${date}${typeParam}`
        )
      );

      const projectCapacityData = await auth.get(
        encodeURI(
          `field/projectCapacity/${projectId}/${stripAndLowercase(territory)}/${format(date, "yyyy-MM-dd")}?${urgent ? "urgent=true" : ""}`
        )
      );

      // eslint-disable-next-line no-console
      console.log("projectCapacityData", JSON.stringify(projectCapacityData));
      // eslint-disable-next-line no-console
      console.log("svisData", JSON.stringify(svisData));
      // eslint-disable-next-line no-console
      console.log("--- Individual Decisions ---")

      const data = projectCapacityData.Payload.map((pcd) => {
        const svisPayload = flattenPayload(svisData.filter(compareDates(pcd)));
        const svisAM = svisPayload?.AM || [];
        const svisPM = svisPayload?.PM || [];

        const amCapacity = pcd.AM;
        const pmCapacity = pcd.PM;

        const amUsed = svisAM.filter((am) => am.ProjectId === projectId).length || 0;
        const pmUsed = svisPM.filter((pm) => pm.ProjectId === projectId).length || 0;

        const amAvailable = amCapacity - amUsed;
        const pmAvailable = pmCapacity - pmUsed;

        // eslint-disable-next-line no-console
        console.log(`
-- Base Data/Calculations ${pcd.EventDate.slice(0, 10)} & projectId: ${projectId} --
I am telling the project-capacity API that this appointment is ${urgent ? "urgent" : "not urgent"}
From the project-capacity API, I can see that AM capacity is ${amCapacity} and PM capacity is ${pmCapacity}
Based on the SVIS data, I can see that there are ${amUsed} AM agreed appointments and ${pmUsed} PM agreed appointments
Subtracting agreed appointments from capacity, I am calculating AM availability as ${amAvailable} and PM availability as ${pmAvailable}

--- Recommendation (Same-Site) Engine ---
${isRecommended(svisAM, siteId, projectId, workItemTemplateRef) ? "I think that the AM slot should be recommended because the siteId matches and the template is DomesticFieldReactive" : `I think that the AM slot should not be recommended because ${isRecommendedText(svisAM, siteId, projectId, workItemTemplateRef)}`}
${isRecommended(svisPM, siteId, projectId, workItemTemplateRef) ? "I think that the PM slot should be recommended because the siteId matches and the template is DomesticFieldReactive" : `I think that the PM slot should not be recommended because ${isRecommendedText(svisPM, siteId, projectId, workItemTemplateRef)}`}
        `);

        return (
          {
            Territory: territory,
            AvailabilityDate: pcd.EventDate,
            AM: {
              available: amAvailable,
              capacity: amCapacity,
              recommended: isRecommended(svisAM, siteId, projectId, workItemTemplateRef),
            },
            PM: {
              available: pmAvailable,
              capacity: pmCapacity,
              recommended: isRecommended(svisPM, siteId, projectId, workItemTemplateRef),
            },
          }
        )
      });

      setEngineerAvailability(data);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error)
      setEngineerAvailability([]);
    }
  };

  const updateEngineerAvailability = async (territory, date) => {
    logger.debug(
      "EngineerAvailabilityProvider.updateEngineerAvailability",
      `GET field/engineerAvailability`
    );
    try {
      // This data returns the availability and the capacity 
      // but we want to pull capacity from the field services bridge
      const svisData = await auth.get(
        encodeURI(
          `field/engineerAvailability?territory=${territory}&date=${date}`
        )
      );

      logger.debug(
        "EngineerAvailabilityProvider.updateEngineerAvailability",
        `Retrieved data`
      );

      const data = svisData.map((sd) => {
        const randCapacityNumber = Math.ceil(Math.random() * 10);
        const randUsedNumber = Math.ceil(Math.random() * 3);

        return {
          Territory: sd.Territory,
          AvailabilityDate: sd.AvailabilityDate,
          LastModified: sd.LastModified,
          AM: {
            available: sd.AM.available - sd.AM.used - randUsedNumber, // TODO: Derive from capacity - used
            capacity: sd.AM.available + randCapacityNumber, // TODO: Pull capacity from the new API
            recommended: randCapacityNumber > 5, // TODO: Replace with data from new same-site endpoint
          },
          PM: {
            available: sd.PM.available - sd.PM.used - randUsedNumber, // TODO: Derive from capacity - used
            capacity: sd.PM.available + randCapacityNumber, // TODO: Pull capacity from the new API
            recommended: randCapacityNumber > 5, // TODO: Replace with data from new same-site endpoint
          }
        }
      });

      setEngineerAvailability(data);
      return data;
    } catch (error) {
      logger.debug(
        "EngineerAvailabilityProvider.updateEngineerAvailability",
        `Failed to retrieve data`
      );
      setEngineerAvailability([
        {
          Territory: "Yorkshire",
          Date: "2022-01-28",
          AM: {
            available: 9,
            capacity: 9,
            used: 0,
          },
          LastModified: "2022-01-27T11:05:13.110Z",
          PM: {
            available: 8,
            capacity: 9,
            used: 1,
          },
        },
      ]);
      return [
        {
          Territory: "Yorkshire",
          Date: "2022-01-28",
          AM: {
            available: 9,
            capacity: 9,
            used: 0,
          },
          LastModified: "2022-01-27T11:05:13.110Z",
          PM: {
            available: 8,
            capacity: 9,
            used: 1,
          },
        },
      ];
    }
  };

  const getEngineerAvailability = () => currentEngineerAvailability;

  return (
    <EngineerAvailabilityContext.Provider
      value={{
        getEngineerAvailability,
        updateEngineerAvailability,
        updateEngineerAvailabilityV2,
      }}
    >
      {children}
    </EngineerAvailabilityContext.Provider>
  );
}

EngineerAvailabilityProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useEngineerAvailability = () =>
  useContext(EngineerAvailabilityContext);
